import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ScoreGauge from "@/components/ScoreGauge";
import { StatusBadge } from "@/components/StatusBadge";
import FilePreview from "@/components/FilePreview";
import { ArrowLeft, FileText, ShieldCheck, CheckCircle2, AlertCircle, IdCard, Cpu, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import DeviceInventoryList from "@/components/DeviceInventoryList";
import TesterTierCard from "@/components/TesterTierCard";

export default function UserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [u, setU] = useState(null);
  const [preview, setPreview] = useState({ open: false, fileId: null, label: "" });

  const load = async () => {
    const { data } = await api.get(`/users/${id}`);
    setU(data);
  };
  useEffect(() => { load(); }, [id]);
  if (!u) return <AppLayout title="Loading..."><div>Loading…</div></AppLayout>;

  const setStatus = async (s) => {
    try { await api.patch(`/users/${id}`, { status: s }); toast.success(`Set ${s}`); load(); } catch { toast.error("Failed"); }
  };
  const setRole = async (r) => {
    try { await api.patch(`/users/${id}`, { role: r }); toast.success(`Role: ${r}`); load(); } catch { toast.error("Failed"); }
  };

  return (
    <AppLayout title={u.name} subtitle={`${u.role.toUpperCase()} · ${u.email}`}>
      <button onClick={()=>nav("/users")} className="text-sm text-slate-500 hover:text-slate-900 mb-3 flex items-center gap-1.5" data-testid="btn-back-users"><ArrowLeft className="w-4 h-4"/> Users</button>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 kpi-card">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge kind="user" value={u.status}/>
                <span className="text-[11px] uppercase tracking-wider text-slate-500">{u.role}</span>
              </div>
              <h2 className="font-display text-2xl font-bold text-slate-900">{u.name}</h2>
              <div className="text-sm text-slate-600">{u.email} · {u.phone || "—"}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {[u.country, u.state, u.city].filter(Boolean).join(", ") || "—"}
                {u.timezone ? <> · <span className="uppercase">{u.timezone}</span></> : null}
              </div>
            </div>
            {u.role === "freelancer" && (
              u.avatar_file_id ? (
                <button
                  type="button"
                  onClick={()=>setPreview({open:true, fileId:u.avatar_file_id, label:"Profile picture"})}
                  className="w-20 h-20 shrink-0 rounded-full overflow-hidden border-2 border-slate-200 hover:border-cyan-500 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  title="Preview profile picture"
                  data-testid="profile-avatar"
                >
                  <img
                    src={`${API}/files/${u.avatar_file_id}`}
                    alt={`${u.name}'s profile picture`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <div
                  className="w-20 h-20 shrink-0 rounded-full bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-2xl font-display font-bold text-slate-400"
                  data-testid="profile-avatar-empty"
                  title="No profile picture uploaded"
                >
                  {(u.name || "?").trim().charAt(0).toUpperCase()}
                </div>
              )
            )}
          </div>
          {u.role === "freelancer" && (
            <>
              <div className="grid grid-cols-2 gap-4 mt-5 text-sm">
                <Info label="Experience" value={`${u.years_experience || 0} years`}/>
                <Info label="Availability" value={`${u.availability_hours || 0} hrs/week`}/>
                <Info label="Working days" value={(u.working_days || []).join(", ") || "—"}/>
                <Info label="Languages" value={formatLanguages(u.languages, u.language_proficiency) || "—"}/>
                <Info label="Payment method" value={u.payment_method || "—"}/>
                <Info label="Skills" value={formatSkills(u.testing_skills, u.skill_years) || "—"}/>
                <Info label="Domains" value={(u.domains || []).join(", ") || "—"}/>
                <Info label="Tools" value={(u.tools || []).join(", ") || "—"}/>
                {u.custom_skills && <Info label="Other skills" value={u.custom_skills}/>}
                {u.custom_tools && <Info label="Other tools" value={u.custom_tools}/>}
                {u.custom_domains && <Info label="Other domains" value={u.custom_domains}/>}
                {u.portfolio_url && <Info label="Portfolio" value={<a className="text-cyan-700 hover:underline" href={u.portfolio_url} target="_blank" rel="noreferrer">link</a>}/>}
                {u.linkedin_url && <Info label="LinkedIn" value={<a className="text-cyan-700 hover:underline" href={u.linkedin_url} target="_blank" rel="noreferrer">profile</a>}/>}
                {u.resume_file_id && <Info label="Resume" value={<a className="text-cyan-700 hover:underline flex items-center gap-1" href={`${API}/files/${u.resume_file_id}`} target="_blank" rel="noreferrer"><FileText className="w-3.5 h-3.5"/> view</a>}/>}
              </div>

              {/* Structured device inventory */}
              <div className="mt-6 pt-5 border-t border-slate-100">
                <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-3">
                  <Cpu className="w-4 h-4 text-cyan-700"/> Devices
                </h3>
                <DeviceInventoryList
                  value={u.device_inventory || []}
                  emptyText="This tester has not added any structured devices yet."
                  testid="admin-device-inventory"
                />
                {/* Legacy device fields */}
                {((u.device_types || []).length > 0
                  || (u.device_manufacturers || []).length > 0
                  || (u.os_versions || []).length > 0
                  || (u.additional_devices || []).length > 0) && (
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm" data-testid="admin-legacy-devices">
                    {(u.device_types || []).length > 0 && <Info label="Device types" value={u.device_types.join(", ")}/>}
                    {(u.device_manufacturers || []).length > 0 && <Info label="Manufacturers" value={u.device_manufacturers.join(", ")}/>}
                    {(u.os_versions || []).length > 0 && <Info label="OS versions" value={u.os_versions.join(", ")}/>}
                    {(u.additional_devices || []).length > 0 && <Info label="Additional devices" value={u.additional_devices.join(", ")}/>}
                  </div>
                )}
              </div>

              {/* Availability schedule */}
              {(u.availability_schedule || []).length > 0 && (
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-3">
                    <CalendarClock className="w-4 h-4 text-cyan-700"/> Weekly schedule
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="admin-availability-schedule">
                    {(u.availability_schedule || []).map((a, i) => (
                      <div key={i} className="border border-slate-200 rounded-md px-2 py-1">
                        <b className="text-slate-900">{a.day}</b> <span className="text-slate-500">· {a.slot}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="space-y-4">
          {u.role === "freelancer" && (
            <>
              <div className="kpi-card flex flex-col items-center py-6">
                <ScoreGauge
                  score={u.tester_score ?? 0}
                  tier={u.tester_tier || u.tester_level}
                  state={u.tester_state || (u.tester_score == null ? "unrated" : "established")}
                />
              </div>
              <TesterTierCard
                tier={u.tester_tier || u.tester_level || "Unrated"}
                score={u.tester_score ?? null}
                state={u.tester_state || (u.tester_score == null ? "unrated" : "established")}
                completedCount={u.score_metrics?.completed_tasks ?? 0}
              />
            </>
          )}
          {user.role === "admin" && u.role === "freelancer" && (
            <div className="kpi-card" data-testid="kyc-review">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-700"/> KYC review</h3>
                <span className="pill bg-slate-100 text-slate-700 border-slate-200">{u.status}</span>
              </div>
              <div className="space-y-2.5">
                <KycRow ok={!!u.email_verified} label="Email verified" testid="kyc-email" />
                <KycRow ok={u.nda_accepted && u.code_of_conduct_accepted && u.privacy_accepted} label="Agreements accepted (NDA, COC, Privacy)" testid="kyc-agreements" />
                <KycDoc icon={IdCard} label="National ID" file_id={u.national_id_file_id} testid="kyc-national-id" onPreview={()=>setPreview({open:true, fileId:u.national_id_file_id, label:"National ID"})} />
                <KycDoc icon={FileText} label="Resume / CV" file_id={u.resume_file_id} testid="kyc-resume" onPreview={()=>setPreview({open:true, fileId:u.resume_file_id, label:"Resume / CV"})} />
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                Review all documents before approving. Click each file to preview.
              </div>
            </div>
          )}
          {user.role === "admin" && u.role === "freelancer" && (
            <HourlyRateCard user={u} onSaved={load} />
          )}
          {user.role === "admin" && (
            <div className="kpi-card">
              <h3 className="font-display font-bold text-slate-900 mb-3">Admin actions</h3>
              <div className="space-y-2">
                {u.status !== "approved" && <Button onClick={()=>setStatus("approved")} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-approve-user">Approve</Button>}
                {u.status !== "rejected" && <Button onClick={()=>setStatus("rejected")} variant="outline" className="w-full" data-testid="btn-reject-user">Reject</Button>}
                {u.status !== "suspended" && <Button onClick={()=>setStatus("suspended")} variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" data-testid="btn-suspend-user">Suspend</Button>}
                <div className="pt-3 border-t border-slate-100 mt-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Change role</div>
                  <div className="grid grid-cols-3 gap-2">
                    {["admin", "manager", "freelancer"].map(r => (
                      <Button key={r} size="sm" variant={u.role===r?"default":"outline"} onClick={()=>setRole(r)} className={u.role===r?"bg-slate-900":""} data-testid={`btn-role-${r}`}>{r}</Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <FilePreview open={preview.open} onOpenChange={(o)=>setPreview(p=>({...p, open:o}))} fileId={preview.fileId} label={preview.label}/>
    </AppLayout>
  );
}

function Info({ label, value }) {
  return <div><div className="text-[11px] uppercase text-slate-500">{label}</div><div className="font-medium text-slate-900 mt-0.5">{value}</div></div>;
}

function formatLanguages(langs, prof) {
  if (!langs || langs.length === 0) return "";
  return langs.map(l => (prof && prof[l]) ? `${l} (${prof[l]})` : l).join(", ");
}

function formatSkills(skills, years) {
  if (!skills || skills.length === 0) return "";
  return skills.map(s => (years && years[s] != null) ? `${s} (${years[s]}y)` : s).join(", ");
}

function KycRow({ ok, label, testid }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testid}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
      <span className={ok ? "text-slate-900" : "text-amber-700"}>{label}</span>
    </div>
  );
}

function KycDoc({ icon: Icon, label, file_id, testid, onPreview }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={testid}>
      <Icon className={`w-4 h-4 ${file_id ? "text-cyan-700" : "text-slate-400"}`} />
      <span className="text-slate-700 flex-1">{label}</span>
      {file_id ? (
        <button onClick={onPreview} className="text-xs text-cyan-700 hover:underline" data-testid={`${testid}-preview`}>Preview →</button>
      ) : (
        <span className="text-xs text-slate-400">Not uploaded</span>
      )}
    </div>
  );
}

function HourlyRateCard({ user, onSaved }) {
  const [rate, setRate] = useState(String(user.default_hourly_rate ?? ""));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const n = rate === "" ? 0 : Number(rate);
    if (Number.isNaN(n) || n < 0 || n > 1000) {
      toast.error("Rate must be between 0 and 1000");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { default_hourly_rate: n });
      toast.success(`Default rate set to $${n}/hr`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rate");
    } finally { setBusy(false); }
  };
  return (
    <div className="kpi-card" data-testid="hourly-rate-card">
      <div className="flex items-center gap-2 mb-2">
        <DollarSign className="w-4 h-4 text-cyan-700"/>
        <h3 className="font-display font-bold text-slate-900">Default hourly rate</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">Applied by default to every task this tester is assigned. Individual projects and tasks can override it.</p>
      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-sm">$</span>
        <Input
          type="number"
          min={0}
          max={1000}
          step="0.5"
          value={rate}
          onChange={(e)=>setRate(e.target.value)}
          className="max-w-[140px]"
          placeholder="0"
          inputMode="decimal"
          data-testid="input-default-hourly-rate"
        />
        <span className="text-slate-500 text-sm">/ hr</span>
        <Button onClick={save} disabled={busy} className="ml-auto bg-cyan-600 hover:bg-cyan-700" data-testid="btn-save-hourly-rate">Save</Button>
      </div>
    </div>
  );
}
